import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useToast } from '@abyss/web/hooks/useToast';
import { storage } from '@abyss/web/tools/storage';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import mapboxgl from 'mapbox-gl';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { getIsSuperUser } from '../../../common/CareTeamCard/CareTeamUtils';
import { UndoAction } from '../../../common/CareTeamProviderCard/UndoAction';
import { toastSuccessStyle } from '../../../common/ShareThisProvider/ShareThisProvider.style';
import { PortalContext } from '../../../context/PortalContext';
import { useConfig } from '../../../frontends/ProviderSearch/context/Analytics';
import { NewPatientDisclaimer } from '../../../frontends/ProviderSearch/routes/ChoosePCP/ChoosePCPLocation/ChoosePCPLocation.styled';
import { Popup } from '../../../frontends/ProviderSearch/routes/MapView/Popup';
import { getEnableMapPinRedesign } from '../../../frontends/ProviderSearch/routes/MapView/utils';
import { useSaveCareTeam } from '../../../hooks/useCareTeam';
import { useCurrentMember } from '../../../hooks/useCurrentMember';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useHsidUuid } from '../../../hooks/useHsidUuid';
import { useLagoon } from '../../../hooks/useLagoon';
import { FacilityDetails } from '../../../models/FacilityDetails';
import { ProviderDetails } from '../../../models/ProviderDetails';
import { Directions } from '../../../models/RouteDirections';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { handleMapClickLocation } from '../../../utils/directions.utils';
import {
  getRoute,
  mapViewUpdatePin,
  setResultIcon,
  setZoomLevel,
  updateIconSize,
} from '../../../utils/map.utils';
import {
  getProviderLocationWithProviderGroup,
  getSelectedLocationIndex,
} from '../../../utils/providerDetails.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { deriveChipValueIfMissing } from '../../BookmarkButton/Utils';
import { Constants } from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { hideScrollbar, mediumScreenOnly } from '../../ConstantsStyles';
import { getGeoLocationFromStorage } from '../../PSXHeader/SearchBar/utils';
import { handleLinkAndModalTrack } from '../../Utils/adobeTrackUtils';
import { DirectionMapDisplay } from '../DirectionMapDisplay/DirectionMapDisplay';
import { MapViewSelectLocationCard } from '../MapViewSelectLocationCard';
import { CareTeamSelectLocationCard } from '../MapViewSelectLocationCard/CareTeamSelectLocationCard';
import { MapViewStickyButton } from '../MapViewStickyButton';
import {
  ChooseLocationCardsContainer,
  ChooseLocationLabel,
  ChooseLocationRow,
  LocationsWrapColumn,
  MapViewWrap,
  MobileMapViewWrap,
  MobileViewMapButton,
  SidePaneCloseButton,
} from './MapViewLocationsWrap.style';

type Props = {
  hasMultipleLocations: boolean;
  mobileScreen: boolean;
  providerDetails: ProviderDetails | FacilityDetails;
  updateLocationButton: React.ReactNode | React.ReactFragment;
  handleGoBack: () => void;
  useStickyButtonRow: boolean;
  setCurrentIndex: (currentIndex: number) => void;
  directionsButtonClicked: boolean;
  mobileRouteView?: boolean;
  setMobileRouteView: (a: boolean) => void;
  skipNavigationFocus?: boolean;
  updateLocation?: boolean;
  handleUpdateLocation: () => void;
  isSameLocationSelected?: boolean;
  providerSelectedLocation?: string[];
  providerId?: string;
};
export const MapViewLocationsWrap = ({
  mobileScreen,
  providerDetails,
  handleGoBack,
  setCurrentIndex,
  directionsButtonClicked,
  mobileRouteView = false,
  setMobileRouteView,
  skipNavigationFocus,
  updateLocation,
  handleUpdateLocation,
  providerSelectedLocation = [],
  providerId,
}: Props) => {
  const [viewMapEnhancementsFlag, enableMapPinRedesignFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
  ]);
  const [showMapCollapse] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.SHOW_MAP_COLLAPSE,
  ]);

  const { toast } = useToast();
  const currentMember = useCurrentMember();
  const [viewDirection, setViewDirections] = useState<boolean>(false);
  const backBtnRef = React.useRef<HTMLAnchorElement>(null);
  const [closeSidePanel, setCloseSidePanel] = useState<boolean>(false);
  const [routeEndCoords, setRouteEndCoords] = useState<
    [number | null, number | null]
  >([null, null]);
  const [directions, setDirections] = useState<Directions>({
    userPlaceName: '',
    endPlaceName: '',
    routes: [],
  });
  const allConfigs = useLagoon('config');
  const isSuperUser = getIsSuperUser(allConfigs);

  const [localProviderSelectedLocation, setLocalProviderSelectedLocation] =
    useState(providerSelectedLocation);

  const [defaultLocalSelectedLocation] = useState(providerSelectedLocation);

  const hsidUuid = useHsidUuid();

  const mediumScreen = useMediaQuery(mediumScreenOnly);
  const map = React.useRef<mapboxgl.Map | null>(null);
  const { t } = useTranslation();
  const { longitude, latitude } = getGeoLocationFromStorage();
  const [highlightId, setHighlightId] = useSessionStorage<any>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_HIGHLIGHT_ID,
    { providerId: '', from: '' }
  );
  const [selectedId, setSelectedId] = useState<string>('');
  const providerLocations = useMemo(
    () =>
      getProviderLocationWithProviderGroup(providerDetails?.providerLocations),
    [providerDetails?.providerLocations]
  );

  const [mapPinCoords] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_COORDS,
    []
  );

  const { coverageType, chipValue, originalChipValue, setChipValue } =
    useChipStore(
      useShallow((state: ChipState) => ({
        coverageType: state.coverageType,
        chipValue: state.chipValue,
        originalChipValue: state.originalChipValue,
        setChipValue: state.setChipValue,
      }))
    );
  const urlParams = new URLSearchParams(window.location.search);
  const sectionType = urlParams.get('sectionType');
  const pcpIndicator = urlParams.get('pcpIndicator') === 'true';
  const [derivedChipValue, setDerivedChipValue] = useState('');
  useEffect(() => {
    const derivedChipValueLocal = deriveChipValueIfMissing(
      chipValue,
      undefined,
      coverageType,
      sectionType,
      pcpIndicator
    );
    setDerivedChipValue(derivedChipValueLocal);
    setChipValue(derivedChipValueLocal);
  }, [chipValue, coverageType, sectionType, pcpIndicator]);

  const selectedIdIndex = selectedId
    ? providerLocations?.findIndex(
        (location) => selectedId === location?.locationId
      ) > -1
      ? providerLocations?.findIndex(
          (location) => selectedId === location?.locationId
        )
      : 0
    : 0;

  const highlightRefs = React.useRef<any>({});
  const mobileMapViewClassName = mobileScreen ? 'location-card-map-view' : '';

  useEffect(() => {
    highlightRefs.current[highlightId.providerId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }, [highlightId]);

  useEffect(() => {
    // Back button focus on the first render
    if (backBtnRef.current) backBtnRef.current.focus();
  }, []);

  useEffect(() => {
    map.current?.resize();
    if (!closeSidePanel) {
      setZoomLevel(map.current, mapPinCoords);
    }
  }, [closeSidePanel]);

  const handleMapButton = () => {
    setMobileRouteView(true);
    setViewDirections(true);
  };

  const [, setCareTeamProviders] = useSaveCareTeam({
    onCompleted: (data: any) => {
      if (
        data?.data?.saveCareTeam?.status === '201' ||
        data?.data?.saveCareTeam?.status === '200'
      ) {
        handleGoBack();
        toast.show({
          message: (
            <UndoAction
              message={updateToastContent?.message}
              providerName={''}
            />
          ),
          variant: 'success',
          icon: <IconMaterial color="$white !important" icon="check_circle" />,
          css: toastSuccessStyle,
          autoClose: 7000,
        });
      }
    },
  });

  const selectLocation = (index, location) => {
    const { locationId } = location;
    if (updateLocation) {
      handleLocationUpdate(locationId);
    } else {
      setSelectedId(locationId);
    }
    setCurrentIndex(index);
    const linkName: string = `select location`;
    const linkLocation: string = `body:${Constants.PROVIDER_DETAILS.VIEWONMAP}`;
    const modalName: string = Constants.MAP_VIEW_NAVIGATION_MODAL;
    handleLinkAndModalTrack(linkName, linkLocation, modalName);
  };

  const mapOnPremKey = useConfig('MAPBOX_ONPREM_KEY');
  const mapKey = useConfig('MAPBOX_KEY');
  const mapOnPremURL = useConfig('MAPBOX_ONPREM_URL');
  const { portalData } = useContext(PortalContext);

  const handleSaveLocation = async () => {
    const location = providerDetails?.providerLocations?.[selectedIdIndex];
    if (updateLocation && isSuperUser != null) {
      setCareTeamProviders({
        variables: {
          groupNumber: currentMember?.policyNumber || '',
          providerId,
          isUnsave: false,
          locationIds:
            localProviderSelectedLocation?.length == 1
              ? localProviderSelectedLocation[0]
              : localProviderSelectedLocation,
          coverageType,
          chipValue: derivedChipValue,
          originalChipValue: originalChipValue || derivedChipValue,
          npi: providerDetails?.npi,
          providerType: providerDetails?.providerType,
          providerFirstName:
            providerDetails?.firstName || providerDetails?.providerName,
          providerMiddleName: providerDetails?.middleName,
          providerLastName: providerDetails?.lastName,
          lastVisitedDate: null,
          isSuperUser,
          ...(hsidUuid && { userId: hsidUuid }),
        },
      });
    } else {
      const { longitude: locationLng, latitude: locationLat } = location;
      const routeDirections = await getRoute(
        map.current,
        longitude,
        latitude,
        +locationLng,
        +locationLat,
        mediumScreen,
        'driving-traffic',
        0,
        mapOnPremKey,
        mapKey,
        mapOnPremURL,
        portalData?.portalConfig?.mapLocationUrl
      );
      if (routeDirections) setDirections(routeDirections);
      setRouteEndCoords([Number(locationLng), Number(locationLat)]);
      if (mobileScreen) {
        setMobileRouteView(true);
        setViewDirections(true);
      }
    }
    handleUpdateLocation();
  };

  const handleLocationCardMouseEnter = async (
    index,
    location,
    flyToPin = false
  ) => {
    if (mobileScreen) return;
    await setZoomLevel(map?.current, mapPinCoords);

    if (viewMapEnhancementsFlag) {
      const marker = document.querySelector(
        `#custom-marker-${location.locationId}`
      );
      updateIconSize(true, marker, true);
    }
    setResultIcon(
      map.current,
      index,
      true,
      setHighlightId,
      location.locationId
    );

    if (flyToPin) {
      map.current?.flyTo({
        center: [+location.longitude, +location.latitude],
        zoom: Constants.DEVICE_LOCATION.ZOOM,
      });
    }
    mapViewUpdatePin(
      {
        providerId: location.locationId,
        address: getProviderLocation(location.locationId, providerLocations)
          ?.address,
        providerName: getProviderName(location.locationId, providerLocations),
        latitude: location.latitude,
        longitude: location.longitude,
      },
      flyToPin,
      popupContent,
      setPopupContent,
      map
    );
  };

  const handleLocationCardMouseLeave = (index, location) => {
    if (viewMapEnhancementsFlag) {
      const marker = document.querySelector(
        `#custom-marker-${location.locationId}`
      );
      updateIconSize(false, marker, true);
    }
    if (!selectedId) {
      setResultIcon(map.current, index, false, setHighlightId, null);
    }
  };

  const displayRoute = async (locationLng, locationLat) => {
    const routeDirections = await getRoute(
      map.current,
      longitude,
      latitude,
      +locationLng,
      +locationLat,
      false,
      'driving-traffic',
      0,
      mapOnPremKey,
      mapKey,
      mapOnPremURL,
      portalData?.portalConfig?.mapLocationUrl
    );
    if (routeDirections) setDirections(routeDirections);
  };

  useEffect(() => {
    if (directionsButtonClicked && map.current) {
      const {
        locationId,
        longitude: locationLng,
        latitude: locationLat,
      } = providerLocations[selectedIdIndex];
      setRouteEndCoords([Number(locationLng), Number(locationLat)]);
      setSelectedId(locationId);
      map.current?.on('load', () => {
        mobileScreen && displayRoute(locationLng, locationLat);
      });
    }
  }, [map.current]);

  const handleLocationUpdate = (locationId: string) => {
    if (updateLocation) {
      const isSelected = localProviderSelectedLocation?.includes(locationId);
      const updatedLocations = isSelected
        ? localProviderSelectedLocation.filter(
            (selectedLocation) => selectedLocation !== locationId
          )
        : [...localProviderSelectedLocation, locationId];
      setLocalProviderSelectedLocation(updatedLocations);
    }
  };

  const chooseALocationHeading = (
    <ChooseLocationRow
      data-auto-testid="choose-a-location-row"
      data-testid="choose-a-location-row"
    >
      <ChooseLocationLabel color="$primary1" display="h4" offset={1}>
        {updateLocation
          ? t('CHOOSE_LOCATIONS')
          : t('CHOOSE_PCP_FLOW.CHOOSE_A_LOCATION')}
      </ChooseLocationLabel>
      {mobileScreen && updateLocation && (
        <MobileViewMapButton
          before={
            <IconMaterial
              color="$interactive1"
              icon="map"
              isScreenReadable
              size="15px"
            />
          }
          data-auto-testid="map-button"
          data-testid="map-button"
          onClick={handleMapButton}
          variant="ghost"
        >
          {t('View Map')}
        </MobileViewMapButton>
      )}
    </ChooseLocationRow>
  );

  const wrapperSelectedStyle = {
    border: '3px solid #002677',
    borderRadius: '12px',
  };

  const handleClickLocation = async (locationId, locationLng, locationLat) => {
    await handleMapClickLocation(
      map.current,
      selectedId,
      longitude,
      latitude,
      locationId,
      locationLng,
      locationLat,
      mapOnPremKey,
      mapKey,
      mapOnPremURL,
      portalData?.portalConfig?.mapLocationUrl,
      mobileScreen,
      setDirections,
      setRouteEndCoords,
      setSelectedId
    );
  };

  const lagoonData = useLagoon('ui-messaging')();
  const newPatientDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.CHOOSE_PCP_FLOW.NEW_PATIENT_DISCLAIMER,
  });

  const modalContent = find(lagoonData, {
    key: ConstantsLagoon.SAVE_PROVIDER_LOCATION_MODAL
      .SAVE_PROVIDER_LOCATION_MODAL_CONTENT,
  });

  const updateToastContent = find(lagoonData, {
    key: ConstantsLagoon.UPDATE_PROVIDER_TOAST.UPDATE_PROVIDER_TOAST_MESSAGE,
  });

  const getProviderLocation = (providerId, locations) =>
    locations.find((location) => location.locationId === providerId);

  const getProviderName = (providerId, locations) => {
    const providerLocation = locations.find(
      (location) => location.locationId === providerId
    );
    if (providerLocation) {
      return providerLocation.providerGroupsAffiliations?.[0]?.value;
    }
    return '';
  };

  const [popupContent, setPopupContent] = useState<any>('');

  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );

  const enableMapPinRedesign: boolean = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );

  const updatePin = (practitioner, flyToPin = false) => {
    if (enableMapPinRedesign) {
      if (!practitioner) {
        mapViewUpdatePin(null, false, popupContent, setPopupContent, map);
        return;
      }

      return;
    }

    if (mobileScreen) {
      setPopupContent({ ...practitioner });
      return;
    }
    mapViewUpdatePin(
      practitioner,
      flyToPin,
      popupContent,
      setPopupContent,
      map
    );
  };

  return (
    <>
      <MobileMapViewWrap
        className={mobileScreen ? 'show-mobile-map-view' : ''}
        data-testid="map-view-wrap-locations"
        style={{ paddingRight: viewMapEnhancementsFlag ? '0' : '' }}
      >
        {!viewDirection && (
          <div
            style={{
              paddingBottom: updateLocation
                ? mobileScreen
                  ? '20px'
                  : '40px'
                : 0,
            }}
          >
            <Heading display="h3" style={{ padding: '20px 0px' }}>
              {updateLocation ? t('PROVIDER_LOCATION') : t('GET_DIRECTION')}
            </Heading>
            {updateLocation ? (
              <Text color="rgba(75, 77, 79, 1)" size="$lg">
                {modalContent?.message}
              </Text>
            ) : null}
          </div>
        )}
        {!viewDirection && (
          <LocationsWrapColumn
            className="locations-wrap-column"
            css={hideScrollbar}
          >
            {!closeSidePanel && (
              <div>
                {chooseALocationHeading}
                <ChooseLocationCardsContainer
                  alignItems="flex-end"
                  aria-label={t('ACCESSIBILITY_LABELS.LOCATIONS')}
                  className="choose-location-container"
                  role="group"
                >
                  {providerLocations?.map((location, index) => (
                    <div
                      aria-pressed={selectedId === location.locationId}
                      className="location-cards-container"
                      data-auto-testid="provider-location-section"
                      data-testid={`provider-location-section-${index}`}
                      key={location.locationId}
                      onBlur={() =>
                        handleLocationCardMouseLeave(index, location)
                      }
                      onFocus={() =>
                        handleLocationCardMouseEnter(index, location)
                      }
                      onMouseEnter={() =>
                        handleLocationCardMouseEnter(index, location)
                      }
                      onMouseLeave={() =>
                        handleLocationCardMouseLeave(index, location)
                      }
                      style={
                        updateLocation
                          ? localProviderSelectedLocation?.includes(
                              location.locationId
                            )
                            ? wrapperSelectedStyle
                            : {}
                          : selectedId === location.locationId
                          ? wrapperSelectedStyle
                          : {}
                      }
                    >
                      {updateLocation ? (
                        <CareTeamSelectLocationCard
                          className={`
                              ${mobileMapViewClassName} ${
                            highlightId.providerId === location.locationId
                              ? 'highlight'
                              : ''
                          }
                            `}
                          handleDirectionClick={() => {}}
                          index={index}
                          isDisabled={
                            localProviderSelectedLocation?.length >= 5 &&
                            !localProviderSelectedLocation?.includes(
                              location.locationId
                            )
                          }
                          isFromCareTeam
                          location={location}
                          selectLocation={() => selectLocation(index, location)}
                          selected={localProviderSelectedLocation?.includes(
                            location?.locationId
                          )}
                          showBadgeRow
                          totalLocations={providerLocations?.length}
                        />
                      ) : (
                        <MapViewSelectLocationCard
                          className={`
                      ${mobileMapViewClassName} ${
                            highlightId.providerId === location.locationId
                              ? 'highlight'
                              : ''
                          }
                    `}
                          handleDirectionClick={() => {}}
                          highlightRefs={highlightRefs}
                          index={index}
                          isDisabled={
                            localProviderSelectedLocation?.length >= 5
                          }
                          location={location}
                          selectLocation={() => selectLocation(index, location)}
                          selected={selectedId === location.locationId}
                          showBadgeRow
                          totalLocations={providerLocations?.length}
                        />
                      )}
                    </div>
                  ))}
                </ChooseLocationCardsContainer>
                <MapViewStickyButton
                  handleCancel={handleGoBack}
                  handleSaveLocation={handleSaveLocation}
                  isSameLocationSelected={
                    updateLocation
                      ? localProviderSelectedLocation.length === 0 ||
                        JSON.stringify(localProviderSelectedLocation) ===
                          JSON.stringify(defaultLocalSelectedLocation)
                      : false
                  }
                  updateLocation={updateLocation}
                />

                <NewPatientDisclaimer
                  data-auto-testid="patient-disclaimer"
                  data-testid="patient-disclaimer"
                >
                  <Text fontWeight="$medium" size={12.64}>
                    {newPatientDisclaimer?.message ?? ''}
                  </Text>
                </NewPatientDisclaimer>
              </div>
            )}
            {showMapCollapse ? (
              <SidePaneCloseButton
                alignItems="center"
                className="side-btn"
                justify="center"
              >
                <IconMaterial
                  css={{
                    fill: '$black',
                  }}
                  data-auto-testid="sidepanel-close-icon"
                  data-testid="sidepanel-close-icon"
                  icon={
                    closeSidePanel
                      ? 'keyboard_arrow_right'
                      : 'keyboard_arrow_left'
                  }
                  onClick={() => {
                    setCloseSidePanel(!closeSidePanel);
                    adobeLinkTrackEvent({
                      name: `sidepanel:${closeSidePanel ? 'open' : 'close'}`,
                      location: 'body:map',
                      type: 'internal',
                    });
                  }}
                  size="$md"
                />
              </SidePaneCloseButton>
            ) : null}
          </LocationsWrapColumn>
        )}
        <MapViewWrap
          cssProps={{
            viewMapEnhancementsFlag,
            closeSidePanel,
            mobileRouteView,
          }}
        >
          {((mobileScreen && viewDirection) || !mobileScreen) &&
          providerLocations?.length ? (
            <DirectionMapDisplay
              closeMapView={() => {
                if (mobileScreen && viewDirection) {
                  setViewDirections(false);
                  setMobileRouteView(false);
                }
              }}
              coords={{ longitude, latitude }}
              directions={directions}
              fullWidth={viewMapEnhancementsFlag}
              isLocationsTab
              locationResults={providerLocations}
              map={map}
              mobileRouteView={mobileRouteView}
              mobileScreen={mobileScreen}
              refToBeFocusedOnDirectionsClose={
                highlightRefs.current[highlightId.providerId] as HTMLElement
              }
              routeEndCoords={routeEndCoords}
              selectLocation={handleClickLocation}
              setPopupContent={setPopupContent}
              setRouteEndCoords={setRouteEndCoords}
              skipNavigationFocus={skipNavigationFocus}
              updatePin={updatePin}
            />
          ) : null}
        </MapViewWrap>
      </MobileMapViewWrap>
      {!mobileScreen && (
        <React.Fragment>
          <Popup
            handleClickLocation={handleClickLocation}
            index={getSelectedLocationIndex(
              providerLocations,
              highlightId.providerId
            )}
            isChooseLocation={true}
            practitioner={{
              providerId: highlightId.providerId,
              address: getProviderLocation(
                highlightId.providerId,
                providerLocations
              )?.address,
              providerName: getProviderName(
                highlightId.providerId,
                providerLocations
              ),
            }}
            selectLocation={() =>
              selectLocation(
                getSelectedLocationIndex(
                  providerLocations,
                  highlightId.providerId
                ),
                getProviderLocation(highlightId.providerId, providerLocations)
              )
            }
            selected={selectedId === highlightId.providerId}
            totalLocations={providerLocations?.length}
            updatePin={updatePin}
          />
        </React.Fragment>
      )}
    </>
  );
};
